// "Install app" (HARDEN-pwa). Chromium browsers fire `beforeinstallprompt` once the app is
// installable (manifest, icons, a service worker); the event is kept so a button can show the
// browser's own prompt later. Safari on iPhone and iPad has no prompt: there the entry explains
// Share › Add to Home Screen. An app already running installed (display-mode standalone, or
// iOS's `navigator.standalone`) shows no entry. `startInstallCapture()` runs at startup
// (main.tsx), before any screen, because the event can fire before the settings page mounts.
import { useSyncExternalStore } from "react";

/** The non-standard event (Chromium); typed here as far as the app uses it. */
export type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export type InstallState =
  /** Running as an installed app: no entry. */
  | { kind: "installed" }
  /** The browser offers its install prompt. */
  | { kind: "available"; install: () => Promise<"accepted" | "dismissed"> }
  /** iPhone / iPad Safari: Share › Add to Home Screen. */
  | { kind: "ios" }
  /** Nothing to offer (not installable here, or already declined this session). */
  | { kind: "unavailable" };

type Env = {
  matchMedia?: ((q: string) => { matches: boolean }) | undefined;
  userAgent: string;
  maxTouchPoints?: number | undefined;
  standalone?: boolean | undefined;
};

export function isStandalone(env: Env): boolean {
  return Boolean(env.standalone) || Boolean(env.matchMedia?.("(display-mode: standalone)").matches);
}

/** iPhone, iPod or iPad (iPadOS reports a Mac with touch) in a browser without an install prompt. */
export function isIos(env: Env): boolean {
  return (
    /iPhone|iPad|iPod/.test(env.userAgent) ||
    (/Macintosh/.test(env.userAgent) && (env.maxTouchPoints ?? 0) > 1)
  );
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
let state: InstallState = { kind: "unavailable" };
const listeners = new Set<() => void>();

function envNow(): Env {
  const nav = navigator as Navigator & { standalone?: boolean };
  return {
    matchMedia: typeof window.matchMedia === "function" ? (q) => window.matchMedia(q) : undefined,
    userAgent: nav.userAgent,
    maxTouchPoints: nav.maxTouchPoints,
    standalone: nav.standalone,
  };
}

function recompute(): void {
  const env = envNow();
  state =
    installed || isStandalone(env)
      ? { kind: "installed" }
      : deferred
        ? { kind: "available", install }
        : isIos(env)
          ? { kind: "ios" }
          : { kind: "unavailable" };
  for (const l of listeners) l();
}

async function install(): Promise<"accepted" | "dismissed"> {
  const e = deferred;
  if (!e) return "dismissed";
  deferred = null; // a prompt can be shown once
  await e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === "accepted") installed = true;
  recompute();
  return outcome;
}

let started = false;
/** Listens for the browser's install events for the whole session (idempotent). */
export function startInstallCapture(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault(); // no mini-infobar: the app offers it where it belongs
    deferred = event as BeforeInstallPromptEvent;
    recompute();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    recompute();
  });
  recompute();
}

/** Test seam: forget the captured state. */
export function resetInstallCapture(): void {
  deferred = null;
  installed = false;
  recompute();
}

export function useInstallState(): InstallState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state,
  );
}
